import React from 'react'
import classNames from 'classnames'
import styled from 'styled-components'
import { Icon, Ref } from 'semantic-ui-react'
import * as R from 'ramda'

/**
 * @template T
 * @typedef {import('shared/components/FilterGroup/reducer').Rule<T>} Rule
 */

// PET-1963: conditions that use the relative-date encoded value format
const RELATIVE_DATE_CONDITIONS = ['is_before', 'is_on_or_after']
const VALUELESS_COMPARISON_LABELS = {
  is_unknown: 'is unknown',
  has_any_value: 'has any value',
}

/**
 * Decode a relative-date encoded value like '30_days_ago' or '7_days_from_now'
 * into a human-readable string like '30 days ago' or '7 days from now'.
 * Returns null if the value does not match the expected format.
 */
function decodeRelativeDateValue(encodedValue) {
  if (!encodedValue) {
    return null
  }
  const match = /^(\d+)_days_(ago|from_now)$/.exec(encodedValue)
  if (!match) {
    return null
  }
  const n = match[1]
  const direction = match[2] === 'ago' ? 'ago' : 'from now'
  return `${n} days ${direction}`
}

export const FilterLabel = React.forwardRef(
  /**
   * @template T
   * @param {object} props
   * @param {import('shared/components/FilterGroup/reducer').Rule<T>} props.rule
   * @param {boolean} props.active
   * @param {boolean} props.isValueMissing
   * @param {function} props.onClick
   * @param {function} props.onRemove
   * @param {object[]} props.comparisons
   * @param {boolean=} props.newAdvancedFilter
   * @param {number=} props.rulesLength
   * @param {boolean=} props.isShowDeleteIcon
   */
  (
    {
      rule,
      active,
      isValueMissing,
      onClick,
      onRemove,
      comparisons,
      newAdvancedFilter,
      rulesLength,
      isShowDeleteIcon = true,
    },
    ref
  ) => {
    const color = isValueMissing ? 'red' : active ? 'blue' : undefined
    const iconColor = isValueMissing ? '#b90016' : active ? '#007eff' : '#dee6f0'

    return (
      <Ref innerRef={ref}>
        <div className="relative flex items-center h-full group">
          <Label
            className={classNames(
              `ui basic label !flex !justify-center !items-center h-full group-hover:text-[#4a4a4a] mx-1 ${
                newAdvancedFilter ? '!mr-0' : ''
              }`, // margin-right here only when in filter group
              color
            )}
            onClick={onClick}
            newAdvancedFilter={newAdvancedFilter}
            rulesLength={rulesLength}
          >
            {/* Adding padding of 1px is a hack I found that makes the icon not get cut off */}
            <Icon
              className="!mx-1 pl-[1px] !group-hover:fill-[#4a4a4a]"
              name={rule.icon}
              style={{ color: iconColor }}
            />
            <div
              className={classNames('fade-container h-full flex items-center', {
                // Base colors (non-hover states)
                'text-blue-500': active && !isValueMissing,
                'text-red-500': isValueMissing,
                'text-gray-500': !active && !isValueMissing,

                // Hover gradient effects (conditionally added when isShowDeleteIcon is true)
                ...(isShowDeleteIcon && {
                  'group-hover:bg-gradient-to-r from-[#007eff] to-white group-hover:text-transparent group-hover:bg-clip-text':
                    active && !isValueMissing,
                  'group-hover:bg-gradient-to-r from-[#b90016] to-white group-hover:text-transparent group-hover:bg-clip-text':
                    isValueMissing,
                  'group-hover:bg-gradient-to-r from-[#4a4a4a] to-white group-hover:text-transparent group-hover:bg-clip-text':
                    !active && !isValueMissing,
                }),
              })}
              style={{
                transition: 'opacity 0.5s ease-in-out', // Fade effect for both elements
              }}
            >
              <strong className="mr-1">{rule.label}</strong>
              <FilterValue
                className="inline-block mr-1"
                rule={rule}
                isValueMissing={isValueMissing}
                comparisons={comparisons}
              />
            </div>
          </Label>
          {isShowDeleteIcon && (
            <span
              className={`absolute invisible p-1 text-black rounded-full cursor-pointer group-hover:visible ${
                rulesLength && rulesLength > 1 ? 'top-[-0.1rem] right-0' : 'top-[0.25rem] right-0'
              }`}
              onClick={onRemove}
            >
              <i className="icon fas fa-xmark" style={{ margin: 0 }} />
            </span>
          )}
        </div>
      </Ref>
    )
  }
)

const Label = styled.a`
  display: inline-block;
  padding: ${(props) =>
    props.newAdvancedFilter && props.rulesLength > 1 ? '4px !important' : '12px !important'};
  user-select: none;
  border: 1px solid #dee6f0 !important;
  border-top-right-radius: ${(props) =>
    props.newAdvancedFilter && props.rulesLength == 1 ? '0 !important' : '4px'};
  border-bottom-right-radius: ${(props) =>
    props.newAdvancedFilter && props.rulesLength == 1 ? '0 !important' : '4px'};
`

/**
 * @template T
 * @param {object} props
 * @param {string=} props.className
 * @param {import('shared/components/FilterGroup/reducer').Rule<T>} props.rule
 * @param {boolean} props.isValueMissing
 * @param { object[]} props.comparisons
 */
function FilterValue({ className, rule, isValueMissing, comparisons }) {
  if (isValueMissing) {
    return <div className={className}>is missing value</div>
  }

  if (!rule.filters) {
    return null
  }

  const labels = rule.filters.map((filter) => filter.label || filter.value)
  if (VALUELESS_COMPARISON_LABELS[rule.comparison]) {
    return <div className={className}>{getValuelessComparisonLabel(rule, comparisons)}</div>
  } else if (RELATIVE_DATE_CONDITIONS.includes(rule.comparison)) {
    // PET-1963: decode relative-date encoded value for human-readable chip display
    const comparisonLabel = getComparisonLabel(rule, comparisons)
    const rawValue = labels[0] || ''
    const decoded = decodeRelativeDateValue(rawValue)
    const displayValue = decoded !== null ? decoded : rawValue
    return <div className={className}>{comparisonLabel + ' ' + displayValue}</div>
  } else {
    const comparisonLabel = getComparisonLabel(rule, comparisons)

    const sentiment = R.path(['extra', 'attribute'], rule)
    const extra =
      rule.field === 'topic.pair.sentiment' && sentiment ? ` + ${sentimentLabel[sentiment]}` : ''

    const fieldValue = formatValueSentence(labels)

    const filterLabel = comparisonLabel + ' ' + fieldValue + extra

    return <div className={className}>{filterLabel}</div>
  }
}

const sentimentLabel = {
  positive: 'Positive',
  negative: 'Negative',
  undefined: 'Neutral',
}

function getValuelessComparisonLabel(rule, comparisons) {
  const matchComparison = R.find(R.propEq('value', rule.comparison))(comparisons || [])
  if (matchComparison) {
    return matchComparison['label']
  }
  return VALUELESS_COMPARISON_LABELS[rule.comparison]
}

function getComparisonLabel(rule, comparisons) {
  const matchComparison = R.find(R.propEq('value', rule.comparison))(comparisons)
  if (matchComparison) {
    return matchComparison['label']
  } else {
    return rule.comparison
  }
}

function formatValueSentence(value, limit = 2) {
  if (value.length > limit) {
    const texts = value.slice(0, limit)
    return texts.join(', ') + ` (and ${value.length - limit} more)`
  }
  return value.join(', ')
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/FilterLabel/index.js